import { useCallback, useContext, useRef, useState } from "react";
import { GeoData } from "@rogoag/shared-ts";
import type { GeoFormat } from "@rogoag/shared-ts/dist/geodata/geojson-converters/geojson-converter";
import type { FeatureCollection } from "geojson";
import JSZip from "jszip";
import { UserContext } from "../../../hooks/UserContext";
import { FileDownloader } from "../../../utilities/downloadFile";
import { detectFormatFromFile, analyzeGeoJSON, generateOutputFilename } from "./GeoFileConverter.helpers";
import type { ConversionStatus, GeoFileConverterViewModel, LoadedFile } from "./GeoFileConverter.types";

export function useGeoFileConverterViewModel(): GeoFileConverterViewModel & { isSuperUser: boolean } {
	const userContext = useContext(UserContext);
	const isSuperUser = userContext?.isSuperUser ?? false;

	const [loadedFiles, setLoadedFiles] = useState<LoadedFile[]>([]);
	const [targetFormat, setTargetFormat] = useState<GeoFormat>("SHP");
	const [status, setStatus] = useState<ConversionStatus>("idle");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const handleFileSelect = useCallback(async (files: FileList | null) => {
		if (!files || files.length === 0) {
			setLoadedFiles([]);
			return;
		}

		setStatus("loading");
		setErrorMessage(null);

		try {
			const results: LoadedFile[] = [];

			for (let i = 0; i < files.length; i++) {
				const file = files[i];
				const detectedFormat = detectFormatFromFile(file);
				if (!detectedFormat) {
					throw new Error(`Unsupported file format: ${file.name}`);
				}

				const geojson: FeatureCollection = await GeoData.toGeoJSON(file);
				const analysis = analyzeGeoJSON(geojson);

				results.push({
					file,
					detectedFormat,
					geojson,
					...analysis,
				});
			}

			setLoadedFiles(results);
			setStatus("idle");
		} catch (error) {
			const message = error instanceof Error ? error.message : "Failed to load files";
			setErrorMessage(message);
			setStatus("error");
			setLoadedFiles([]);
		}
	}, []);

	const handleUploadClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleClear = useCallback(() => {
		setLoadedFiles([]);
		setTargetFormat("SHP");
		setStatus("idle");
		setErrorMessage(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	}, []);

	const handleRemoveFile = useCallback((index: number) => {
		setLoadedFiles((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const handleFormatChange = useCallback((format: GeoFormat) => {
		setTargetFormat(format);
	}, []);

	const handleConvert = useCallback(async () => {
		if (loadedFiles.length === 0) {
			setErrorMessage("No files loaded");
			setStatus("error");
			return;
		}

		setStatus("converting");
		setErrorMessage(null);

		try {
			const convertedFiles: File[] = [];

			for (const loaded of loadedFiles) {
				const filename = generateOutputFilename(loaded.file, targetFormat);

				switch (targetFormat) {
					case "SHP": {
						const file = await GeoData.toSHP_File(loaded.geojson, "GeoJSON", filename);
						convertedFiles.push(file);
						break;
					}
					case "GeoJSON": {
						const file = await GeoData.toGeoJSON_File(loaded.geojson, "GeoJSON", filename);
						convertedFiles.push(file);
						break;
					}
					case "KML": {
						const file = await GeoData.toKML_File(loaded.geojson, "GeoJSON", filename);
						convertedFiles.push(file);
						break;
					}
					case "KMZ": {
						const file = await GeoData.toKMZ_File(loaded.geojson, "GeoJSON", filename);
						convertedFiles.push(file);
						break;
					}
				}
			}

			// Download results
			if (convertedFiles.length === 1) {
				FileDownloader.downloadFile(convertedFiles[0], convertedFiles[0].name, false);
			} else {
				const zip = new JSZip();
				for (const file of convertedFiles) {
					zip.file(file.name, file);
				}
				await FileDownloader.downloadZip(zip, "converted-files.zip");
			}

			setStatus("success");
		} catch (error) {
			const message = error instanceof Error ? error.message : "Conversion failed";
			setErrorMessage(message);
			setStatus("error");
		}
	}, [loadedFiles, targetFormat]);

	return {
		loadedFiles,
		targetFormat,
		status,
		errorMessage,
		fileInputRef,
		isSuperUser,
		handleFileSelect,
		handleUploadClick,
		handleClear,
		handleRemoveFile,
		handleFormatChange,
		handleConvert,
	};
}
